import { db } from "@/lib/firebase";
import type { RegionConfig } from "@/lib/regions";
import type { Zone } from "@/lib/zones";
import fallbackRegions from "@/lib/regionsSnapshot.json";
import fallbackZones from "@/lib/zonesSnapshot.json";
import { useQuery } from "@tanstack/react-query";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";

// ── Fetch Functions ─────────────────────────────────────────────────────────

async function fetchAllRegions(): Promise<Record<string, RegionConfig>> {
  try {
    const snap = await getDocs(collection(db, "regions"));
    const result: Record<string, RegionConfig> = {};
    snap.forEach((d) => {
      result[d.id] = d.data() as RegionConfig;
    });
    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Firestore unavailable, using static fallback", error);
    return fallbackRegions as unknown as Record<string, RegionConfig>;
  }
}

async function fetchZones(): Promise<Zone[]> {
  try {
    const snap = await getDoc(doc(db, "config", "zones"));
    if (snap.exists()) {
      return (snap.data() as { zones: Zone[] }).zones;
    }
    return fallbackZones as Zone[];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Firestore unavailable, using static zones fallback", error);
    return fallbackZones as Zone[];
  }
}

// ── Hooks ───────────────────────────────────────────────────────────────────

/** Fetch all regions (cached for the entire session) */
export function useRegions() {
  return useQuery({
    queryKey: ["regions"],
    queryFn: fetchAllRegions,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

/** Fetch a single region by ID (derived from the all-regions cache) */
export function useRegion(regionId: string) {
  return useQuery({
    queryKey: ["regions"],
    queryFn: fetchAllRegions,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    select: (data) => data[regionId] ?? null,
  });
}

/** Fetch the zones navigation tree */
export function useZones() {
  return useQuery({
    queryKey: ["zones"],
    queryFn: fetchZones,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

// ── Helper functions (re-exported from pure module for testability) ──────────

export {
  getCamerasFromRegion,
  regionHasCameras,
  getBreakFromRegion,
  findRegionInZones,
} from "@/lib/regionHelpers";
